import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, TrendingUp, TrendingDown, Phone, MessageSquare, Calendar, User } from 'lucide-react';
import ProspectionForm from './ProspectionForm';
import Pagination from './Pagination';
import CarThumbnail from './CarThumbnail';
import { prospectionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Prospection = () => {
  const { userProfile } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'prospected', 'contacted', 'interested'
  const [usernameFilter, setUsernameFilter] = useState('');
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false
  });

  useEffect(() => {
    loadProspects();
    loadStats();
  }, [currentPage, statusFilter, usernameFilter]);

  // Set default username filter to logged-in user once profile is loaded
  useEffect(() => {
    if (userProfile?.username && !usernameFilter) {
      setUsernameFilter(userProfile.username);
    }
  }, [userProfile]);

  const loadProspects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await prospectionService.getAllProspects({
        page: currentPage,
        per_page: 20,
        status: statusFilter || undefined,
        username: usernameFilter || undefined
      });

      setProspects(data?.prospects || []);
      setPagination((prev) => data?.pagination || prev);
    } catch (err) {
      setError(err.message || 'Error al cargar prospecciones');
      console.error('Error loading prospects:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await prospectionService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleCreateProspect = async (formData) => {
    try {
      await prospectionService.createProspect(formData);
      // Recargar datos
      loadProspects();
      loadStats();
    } catch (error) {
      console.error('Error creating prospect:', error);
      throw error;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const extractDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return 'Link inválido';
    }
  };

  const filteredProspects = prospects.filter((prospect) => {
    const searchLower = (searchTerm || '').toLowerCase();
    const domain = (extractDomainFromUrl(prospect?.permalink) || '').toLowerCase();

    const titleLower = (prospect?.title || '').toLowerCase();
    const permalinkLower = (prospect?.permalink || '').toLowerCase();
    const descriptionLower = (prospect?.description || '').toLowerCase();
    const cityLower = (prospect?.city || '').toLowerCase();

    return (
      titleLower.includes(searchLower) ||
      permalinkLower.includes(searchLower) ||
      domain.includes(searchLower) ||
      descriptionLower.includes(searchLower) ||
      cityLower.includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center space-x-4 mb-6 lg:mb-0">
          <div className="p-3 bg-[#7b8fa8] rounded-xl">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white">
              Prospección
            </h1>
            <p className="text-gray-400 mt-1">
              Gestión de leads y seguimiento de clientes potenciales
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-lg"
        >
          <Plus className="w-5 h-5" />
          <span>Nueva Prospección</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="glass-effect rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_prospects}</p>
                <p className="text-xs text-gray-400 uppercase">Vehículos en seguimiento</p>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Phone className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-400">{stats.contacted}</p>
                <p className="text-xs text-gray-400 uppercase">Contactados</p>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{stats.interested}</p>
                <p className="text-xs text-gray-400 uppercase">Interesados</p>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-400">${stats.avg_price.toLocaleString()}</p>
                <p className="text-xs text-gray-400 uppercase">Precio Promedio</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por link, dominio o comentarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
        >
          <option value="">Todos los estados</option>
          <option value="prospected">Prospectados</option>
          <option value="contacted">Contactados</option>
          <option value="interested">Interesados</option>
        </select>

        <select
          value={usernameFilter}
          onChange={(e) => {
            setUsernameFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-3 bg-[#1c1c1a] border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
        >
          <option value="">Todos los usuarios</option>
          {userProfile?.username && (
            <option value={userProfile.username}>@{userProfile.username} (Yo)</option>
          )}
        </select>
      </div>

      {/* Prospects List */}
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full"></div>
        </div>
      ) : error ? (
        <div className="glass-effect rounded-xl p-8 text-center">
          <p className="text-red-400 mb-2">Error al cargar prospecciones</p>
          <p className="text-gray-400 text-sm">{error}</p>
          <button
            onClick={loadProspects}
            className="mt-4 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
          >
            Reintentar
          </button>
        </div>
      ) : filteredProspects.length === 0 ? (
        <div className="glass-effect rounded-xl p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay prospecciones
          </h3>
          <p className="text-gray-400 mb-4">
            Comienza creando tu primera prospección
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Nueva Prospección
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProspects.map((prospect) => (
              <div key={prospect.id} className="glass-effect rounded-xl p-6">
              <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                {/* Imagen del vehículo */}
                {prospect.image && (
                  <div className="flex-shrink-0 w-full sm:w-48 h-32 sm:h-36">
                    <CarThumbnail
                      imageUrl={prospect.image}
                      title={prospect.title}
                      className="rounded-lg shadow-lg"
                    />
                  </div>
                )}
                
                <div className="flex-1 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <div className="flex flex-col mb-2 sm:mb-0">
                      <h3 className="text-xl font-semibold text-white mb-1">
                        {prospect.title}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <a
                          href={prospect.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#48a877] hover:text-[#48a877] transition-colors underline decoration-dotted"
                        >
                          Ver Vehículo
                        </a>
                        <span className="text-xs text-gray-400">
                          ({extractDomainFromUrl(prospect.permalink)})
                        </span>
                        {prospect.username && (
                          <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
                            <User className="w-3 h-3 mr-1" /> {prospect.username}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {prospect.price && (
                        <span className="text-lg font-bold text-green-400">
                          {prospect.currency_id} {prospect.price.toLocaleString()}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        prospect.status === 'interested' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : prospect.status === 'contacted'
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      }`}>
                        {prospect.status === 'interested' ? 'Interesado' : 
                         prospect.status === 'contacted' ? 'Contactado' : 'Prospectado'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                    {prospect.vehicle_year && (
                      <div className="text-gray-400">
                        <span className="font-medium">Año:</span> {prospect.vehicle_year}
                      </div>
                    )}
                    {prospect.kilometers && (
                      <div className="text-gray-400">
                        <span className="font-medium">Km:</span> {prospect.kilometers}
                      </div>
                    )}
                    {prospect.city && (
                      <div className="text-gray-400">
                        <span className="font-medium">Ciudad:</span> {prospect.city}
                      </div>
                    )}
                    {prospect.phone && (
                      <div className="text-gray-400">
                        <Phone className="w-4 h-4 inline mr-1" />
                        <span className="font-medium">Tel:</span> {prospect.phone}
                      </div>
                    )}
                    <div className="text-gray-400">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {formatDate(prospect.execution_time)}
                    </div>
                  </div>

                  {prospect.description && (
                    <div className="flex items-start space-x-2 text-gray-300">
                      <MessageSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                      <p className="text-sm leading-relaxed">{prospect.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredProspects.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={setCurrentPage}
          totalItems={pagination.total}
          itemsPerPage={pagination.per_page}
        />
      )}

      {/* Form Modal */}
      <ProspectionForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleCreateProspect}
      />
    </div>
  );
};

export default Prospection;
